import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { CaretDown, CaretUp, Sparkle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useT } from '@/i18n/LanguageContext';
import { useAuth, canEditMfg } from '@/auth/AuthContext';
import { CommonRegistrationBlock, FormSection, finalizeNumber } from '@/components/registration';
import { mfgApi, ITEM_TYPES, PROCESSING_TYPES } from './mfgApi';
import { shizaiApi } from '@/pages/shizai/shizaiApi';
import SearchableMaster from './SearchableMaster';
import LookupCombobox from '@/components/ui/lookup-combobox';
import AttachmentsPanel from './AttachmentsPanel';
import { SkuWizard } from '@/components/SkuWizard';

const NONE = '__none__';
const EMPTY = {
  sku: '', name: '', maker_pn: '', drawing_no: '', model_no: '', ref_designators: '',
  type: 'raw', category: '', category_id: '', uom: '個', barcode: '', maker: '', material: '',
  departments: [], supplier_id: '', supplier_name: '', reorder_point: 0, reorder_qty: 0,
  pack_size: '', pack_uom: '', max_stock: '', lot_tracked: false, cost: 0,
  standard_cycle_sec: '', status: 'active',
  processing_type: 'in_house', subcontractor_id: '', subcontract_process: '',
  subcontract_unit_price: '', subcontract_lead_days: '',
  account_code: '', asset_category: '',
  // 共通の登録ブロックと同じ項目名（F1〜F6 で同じ）
  number_value: null, sku_group_id: '', order_category_id: '', order_category_name: '',
  description: '', reference_url: '', location: '', location_id: '',
};

// 共通ブロックで出す項目（仕様 2.6 ＋ TECHS 整合）。
// 在庫品目だけの項目（品目種別・カテゴリの別マスタ・在庫/発注・加工・会計）は
// basicExtra / keepingExtra / extra に差し込む。並びと見出しは全台帳で同じ。
const FIELDS = [
  'name', 'maker', 'notes',
  'number', 'sku_group', 'order_category', 'model_no', 'maker_pn', 'uom',
  'supplier', 'reference_url', 'location',
];
// ＊ と 要入力 の対象。**保存を止める条件は今までどおり**（品名と品番だけ・下の save 参照）。
const REQUIRED = ['name', 'number', 'model_no', 'uom', 'order_category'];

/**
 * F4 · 在庫品目を登録 / 編集。
 *
 * 2026-06 · 共通ブロック（`CommonRegistrationBlock`）に寄せた。
 * かたまりは 基本情報 → TECHS発注に必要な情報 → 保管・管理 → 在庫・発注（台帳固有）。
 * 番号は共通の `NumberField`（在庫品目は品番ルールのみ・手入力不可）。
 * **保存を止める条件は変えていない**（品名／新規の品番）。
 */
export default function ItemFormDialog({ open, onClose, item, onSaved, defaultDepartmentId }) {
  const { t } = useT();
  const { user } = useAuth();
  const canWrite = canEditMfg(user);
  const [form, setForm] = useState(EMPTY);
  const [subcontractors, setSubcontractors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [accountCodes, setAccountCodes] = useState([]);
  const [assetCategories, setAssetCategories] = useState([]);
  const [busy, setBusy] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [packCount, setPackCount] = useState('1');
  // 新規で番号が決まっていないまま保存しようとしたときの確認
  const [numberPromptOpen, setNumberPromptOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const saveAfterIssueRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setAdvancedOpen(!!item);
    setPackCount('1');
    mfgApi.suppliers({ supplier_type: 'subcontractor' }).then(setSubcontractors).catch(() => {});
    mfgApi.itemCategories().then(setCategories).catch(() => {});
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/lookups/uoms`)
      .then((r) => setUoms(r.data || [])).catch(() => {});
    mfgApi.departments().then(setDepartments).catch(() => {});
    shizaiApi.accountCodes().then(setAccountCodes).catch(() => {});
    shizaiApi.assetCategories().then(setAssetCategories).catch(() => {});
    if (item) {
      setForm({
        ...EMPTY, ...item,
        maker_pn: item.maker_pn || '', drawing_no: item.drawing_no || '',
        model_no: item.model_no || '', ref_designators: item.ref_designators || '',
        maker: item.maker || '', material: item.material || '', barcode: item.barcode || '',
        max_stock: item.max_stock ?? '', pack_size: item.pack_size ?? '', pack_uom: item.pack_uom || '',
        standard_cycle_sec: item.standard_cycle_sec ?? '', supplier_id: item.supplier_id || '',
        supplier_name: item.supplier_name || '', category_id: item.category_id || '',
        departments: item.departments || [], processing_type: item.processing_type || 'in_house',
        subcontractor_id: item.subcontractor_id || '', subcontract_process: item.subcontract_process || '',
        subcontract_unit_price: item.subcontract_unit_price ?? '',
        subcontract_lead_days: item.subcontract_lead_days ?? '',
        account_code: item.account_code || '', asset_category: item.asset_category || '',
        // 共通の項目名に写す（保存時に元の項目名へ戻す）
        description: item.notes || '', reference_url: item.mfg_url || '',
        location_id: item.default_location_id || '', location: item.location || '',
        number_value: null,
      });
    } else {
      const initialDepts = defaultDepartmentId && defaultDepartmentId !== 'all' ? [defaultDepartmentId] : [];
      setForm({ ...EMPTY, departments: initialDepts });
    }
  }, [open, item, defaultDepartmentId]);

  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));
  const toggleDept = (id) => setForm((p) => {
    const cur = p.departments || [];
    return { ...p, departments: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] };
  });

  const buildBody = (overrides = {}) => ({
    ...form,
    ...overrides,
    name: (overrides.name ?? form.name).trim(),
    reorder_point: Number(form.reorder_point) || 0,
    reorder_qty: Number(form.reorder_qty) || 0,
    pack_size: (() => {
      if (form.pack_size === '' || form.pack_size === null) return null;
      const pieces = Number(form.pack_size);
      if (!pieces || pieces <= 0) return null;
      const pc = Number(packCount) > 0 ? Number(packCount) : 1;
      return Math.round((pieces / pc) * 10000) / 10000;
    })(),
    pack_uom: (form.pack_uom || '').trim() || null,
    standard_cycle_sec: form.standard_cycle_sec === '' ? null : Number(form.standard_cycle_sec),
    max_stock: form.max_stock === '' ? null : Number(form.max_stock),
    cost: Number(form.cost) || 0,
    supplier_id: form.supplier_id || null,
    maker: form.maker || null,
    material: (form.material || '').trim() || null,
    departments: form.departments || [],
    default_location_id: form.location_id || null,
    category_id: form.category_id || null,
    sku_group_id: form.sku_group_id || form.number_value?.sku_group_code || null,
    order_category_id: form.order_category_id || null,
    order_category_name: form.order_category_name || null,
    acquisition_type: form.acquisition_type || 'existing',
    notes: form.description || null,
    mfg_url: form.reference_url || null,
    subcontractor_id: form.subcontractor_id || null,
    subcontract_unit_price: form.subcontract_unit_price === '' ? null : Number(form.subcontract_unit_price),
    subcontract_lead_days: form.subcontract_lead_days === '' ? null : Number(form.subcontract_lead_days),
    account_code: form.account_code || null,
    asset_category: form.asset_category || null,
  });

  const persist = async (overrides = {}, numberValue) => {
    setBusy(true);
    try {
      const nv = numberValue !== undefined ? numberValue : form.number_value;
      if (!item) {
        // 保存時に正式発番（閉じただけなら番号を消費しない）
        const issued = await finalizeNumber(nv);
        if (issued) {
          overrides = { ...overrides, sku: issued, barcode: issued };
          setForm((p) => ({ ...p, sku: issued, barcode: issued }));
        }
      }
      const body = buildBody(overrides);
      if (item) await mfgApi.updateItem(item.id, body);
      else await mfgApi.createItem(body);
      toast.success(t('mfg.saved')); onSaved?.(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || t('mfg.error')); }
    finally { setBusy(false); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error(t('mfg.item.name')); return; }
    // iter_142 · 新規は品番が必須（品番ルールからの発番のみ）。既存の空欄は
    // 従来どおりそのまま保存できる（後から発番し直せる）。
    const hasNumber = !!(form.number_value?.pending || form.number_value?.number
      || (form.sku || '').trim());
    if (!item && !hasNumber) { setNumberPromptOpen(true); return; }
    await persist();
  };

  const isProcessed = ['processed_part', 'semifinished'].includes(form.type);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby={undefined} data-testid="item-form-dialog">
        <DialogHeader><DialogTitle>{item ? t('mfg.item.edit') : t('mfg.item.new')}</DialogTitle></DialogHeader>

        <CommonRegistrationBlock
          value={form} onChange={setForm} editing={!!item}
          fields={FIELDS} requiredKeys={REQUIRED}
          askAcquisitionType
          register="mfg_item" appCode="seizo" currentNo={item?.sku || '—'}
          testPrefix="item"
          basicExtra={(
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-semibold text-zinc-600">{t('mfg.item.type')}</Label>
                  <Select value={form.type} onValueChange={(v) => setForm((p) => ({ ...p, type: v }))}>
                    <SelectTrigger data-testid="item-type-select" className="h-10 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{ITEM_TYPES.map((tp) => <SelectItem key={tp} value={tp}>{t(`mfg.type.${tp}`)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-semibold text-zinc-600">{t('mfg.item.category')}</Label>
                  <SearchableMaster testid="item-category" value={form.category_id} options={categories} canWrite={canWrite} valueField="id"
                    onChange={(v) => setForm((p) => ({ ...p, category_id: v }))}
                    onCreate={async (nm) => { const c = await mfgApi.createItemCategory({ name: nm }); setCategories((prev) => [...prev, c]); return c; }}
                    labels={{ searchPh: 'mfg.category.searchPh', createNamed: 'mfg.category.createNamed', noMatch: 'mfg.category.noMatch' }} />
                </div>
              </div>
              {isProcessed && (
                <div className="rounded-lg border border-violet-200 bg-violet-50/40 p-3 space-y-3" data-testid="processing-section">
                  <p className="text-xs font-bold text-violet-700">{t('mfg.processing.section')}</p>
                  <div>
                    <Label className="text-xs font-semibold text-zinc-600">{t('mfg.processing.type')}</Label>
                    <Select value={form.processing_type} onValueChange={(v) => setForm((p) => ({ ...p, processing_type: v }))}>
                      <SelectTrigger data-testid="processing-type-select" className="h-10 mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{PROCESSING_TYPES.map((pt) => <SelectItem key={pt} value={pt}>{t(`mfg.processing.${pt}`)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {form.processing_type === 'subcontracted' && (
                    <div className="space-y-3" data-testid="subcontract-fields">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs font-semibold text-zinc-600">{t('mfg.subcontractor')}</Label>
                          <Select value={form.subcontractor_id || NONE} onValueChange={(v) => setForm((p) => ({ ...p, subcontractor_id: v === NONE ? '' : v }))}>
                            <SelectTrigger data-testid="subcontractor-select" className="h-10 mt-1"><SelectValue placeholder={t('mfg.none')} /></SelectTrigger>
                            <SelectContent><SelectItem value={NONE}>{t('mfg.none')}</SelectItem>{subcontractors.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs font-semibold text-zinc-600">{t('mfg.subcontractProcess')}</Label>
                          <Input data-testid="subcontract-process-input" value={form.subcontract_process} onChange={set('subcontract_process')} placeholder="例: メッキ処理" className="h-10 mt-1" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs font-semibold text-zinc-600">{t('mfg.subcontractUnitPrice')}</Label>
                          <Input type="number" value={form.subcontract_unit_price} onChange={set('subcontract_unit_price')} className="h-10 mt-1" />
                        </div>
                        <div>
                          <Label className="text-xs font-semibold text-zinc-600">{t('mfg.subcontractLeadDays')}</Label>
                          <Input type="number" value={form.subcontract_lead_days} onChange={set('subcontract_lead_days')} className="h-10 mt-1" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
          keepingExtra={(
            <div>
              <Label className="text-xs font-semibold text-zinc-600">{t('mfg.item.departments')}</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="item-departments">
                {departments.map((d) => {
                  const sel = (form.departments || []).includes(d.id);
                  return (
                    <button type="button" key={d.id} data-testid={`item-dept-${d.id}`} onClick={() => toggleDept(d.id)}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${sel ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-zinc-600 border-zinc-200 hover:bg-zinc-50'}`}>
                      {d.name}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-zinc-400 mt-1">{t('mfg.item.departmentsHint')}</p>
            </div>
          )}
          extra={(
            <>
              <button type="button" data-testid="item-advanced-toggle" onClick={() => setAdvancedOpen((v) => !v)}
                className="w-full flex items-center justify-between rounded-lg border border-zinc-200 bg-zinc-50 hover:bg-zinc-100 px-3 py-2.5 transition-colors">
                <span className="text-left">
                  <span className="block text-sm font-semibold text-zinc-700">{t('mfg.item.advanced')}</span>
                  <span className="block text-[11px] text-zinc-400">{t('mfg.item.advancedHint')}</span>
                </span>
                {advancedOpen ? <CaretUp size={16} className="text-zinc-500" /> : <CaretDown size={16} className="text-zinc-500" />}
              </button>

              {advancedOpen && (
                <div className="space-y-3" data-testid="item-advanced-section">
                  <FormSection title="在庫・発注" testId="item-section-stock">
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.item.reorderPoint')}</Label><Input type="number" value={form.reorder_point} onChange={set('reorder_point')} className="h-10 mt-1" data-testid="item-reorder-input" /></div>
                      <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.item.reorderQty')}</Label><Input type="number" value={form.reorder_qty} onChange={set('reorder_qty')} className="h-10 mt-1" data-testid="item-reorder-qty-input" /></div>
                      <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.item.maxStock')}</Label><Input type="number" value={form.max_stock} onChange={set('max_stock')} className="h-10 mt-1" /></div>
                      <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.item.cost')}</Label><Input type="number" value={form.cost} onChange={set('cost')} className="h-10 mt-1" /></div>
                      <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.item.cycleTime')}</Label><Input type="number" value={form.standard_cycle_sec} onChange={set('standard_cycle_sec')} className="h-10 mt-1" data-testid="item-cycle-input" placeholder={t('mfg.item.cycleTimePh')} /></div>
                    </div>
                    <div className="rounded-lg border border-orange-100 bg-orange-50/40 p-3 space-y-1.5" data-testid="pack-section">
                      <Label className="text-xs font-semibold text-zinc-600">{t('mfg.item.packSize')}</Label>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Input type="number" min="0" value={form.pack_size} onChange={set('pack_size')} className="h-10 w-24" data-testid="item-pack-size-input" placeholder="例: 5" />
                        <span className="text-sm font-medium text-zinc-700" data-testid="item-pack-baseuom">{form.uom || '個'}</span>
                        <span className="text-sm text-zinc-400 font-semibold">＝</span>
                        <Input type="number" min="0" value={packCount} onChange={(e) => setPackCount(e.target.value)} className="h-10 w-20" data-testid="item-pack-count-input" />
                        <div className="w-40 min-w-[130px]">
                          <SearchableMaster testid="item-pack-uom" value={form.pack_uom} options={uoms} canWrite={canWrite} valueField="name" allowNone
                            onChange={(v) => setForm((p) => ({ ...p, pack_uom: v || '' }))}
                            onCreate={async (nm) => {
                              const { data: u } = await axios.post(
                                `${process.env.REACT_APP_BACKEND_URL}/api/lookups/uoms`, { name: nm });
                              setUoms((prev) => [...prev, u]); return u;
                            }}
                            labels={{ searchPh: 'mfg.uom.searchPh', createNamed: 'mfg.uom.createNamed', noMatch: 'mfg.uom.noMatch' }} />
                        </div>
                      </div>
                      <p className="text-[11px] text-zinc-500">{t('mfg.item.packHint')}</p>
                    </div>
                    <label className="flex items-center gap-2 text-sm text-zinc-700"><Checkbox checked={form.lot_tracked} onCheckedChange={(v) => setForm((p) => ({ ...p, lot_tracked: !!v }))} data-testid="item-lottracked-check" /> {t('mfg.item.lotTracked')}</label>
                  </FormSection>

                  <FormSection title="図面・材質・表示" testId="item-section-drawing">
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.item.drawingNo')}</Label>
                        <Input data-testid="item-drawing-input" value={form.drawing_no} onChange={set('drawing_no')} className="h-10 mt-1 font-mono" placeholder="例: DWG-2026-001 / RevA" /></div>
                      <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.item.barcode')}</Label>
                        <Input data-testid="item-barcode-input" value={form.barcode} onChange={set('barcode')} className="h-10 mt-1 font-mono" /></div>
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-zinc-600">材質</Label>
                      <LookupCombobox kind="materials" value={form.material || ''}
                        onChange={(v) => setForm((p) => ({ ...p, material: v }))}
                        testId="item-material" inputClassName="mt-1 h-10"
                        placeholder="例: SUS304 / A5052 / SS400" />
                    </div>
                  </FormSection>

                  <FormSection title="経理 (Accounting)" testId="item-section-accounting">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs font-semibold text-zinc-600">勘定科目</Label>
                        <Select value={form.account_code || NONE}
                          onValueChange={(v) => setForm((p) => ({ ...p, account_code: v === NONE ? '' : v }))}>
                          <SelectTrigger data-testid="item-account-code-select" className="h-10 mt-1"><SelectValue placeholder="未設定" /></SelectTrigger>
                          <SelectContent className="max-h-80">
                            <SelectItem value={NONE}>未設定</SelectItem>
                            {accountCodes.map((a) => (
                              <SelectItem key={a.code} value={a.code}>
                                {a.code} {a.symbol && `(${a.symbol})`} · {a.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-zinc-600">資産カテゴリ</Label>
                        <Select value={form.asset_category || NONE}
                          onValueChange={(v) => setForm((p) => ({ ...p, asset_category: v === NONE ? '' : v }))}>
                          <SelectTrigger data-testid="item-asset-category-select" className="h-10 mt-1"><SelectValue placeholder="未設定" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NONE}>未設定</SelectItem>
                            {assetCategories.map((c) => (
                              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <p className="text-[11px] text-zinc-500">経理データ出力（月次仕入集計）の分類に使用します。未設定の場合は「その他」として集計されます。</p>
                  </FormSection>
                </div>
              )}

              {item?.id ? (
                <AttachmentsPanel testid="item-attachments" entityType="item" entityId={item.id} canWrite={canWrite}
                  title={t('mfg.attach.itemTitle')} hint={t('mfg.attach.itemHint')} />
              ) : (
                <p className="text-[11px] text-zinc-400" data-testid="item-attach-hint">{t('mfg.attach.afterSave')}</p>
              )}
            </>
          )}
        />

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('mfg.cancel')}</Button>
          <Button data-testid="item-save-btn" onClick={save} disabled={busy} className="bg-teal-700 hover:bg-teal-800">{busy ? t('mfg.saving') : t('mfg.save')}</Button>
        </DialogFooter>

        {/* 新規で品番が決まっていないときの確認（空欄保存はできない） */}
        <AlertDialog open={numberPromptOpen} onOpenChange={setNumberPromptOpen}>
          <AlertDialogContent data-testid="item-sku-autoissue-prompt">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <Sparkle weight="fill" className="text-amber-500" size={20} />
                品番を発番してください
              </AlertDialogTitle>
              <AlertDialogDescription>
                品番は必須です。品番ルールから発番してください（手入力・空欄保存はできません）。
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="gap-2">
              <AlertDialogCancel data-testid="item-sku-autoissue-cancel">キャンセル</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => { setNumberPromptOpen(false); saveAfterIssueRef.current = true; setWizardOpen(true); }}
                className="bg-teal-700 hover:bg-teal-800"
                data-testid="item-sku-autoissue-confirm">
                <Sparkle weight="fill" size={14} className="mr-1" />
                品番ルールを開く
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <SkuWizard
          open={wizardOpen}
          onOpenChange={setWizardOpen}
          defaultApp="seizo"
          defaultDept={(typeof window !== 'undefined' && localStorage.getItem('mfg_active_department')) || ''}
          onIssued={(sku, meta) => {
            if (!sku) return;
            const next = {
              mode: 'template', number: sku, template_code: meta?.template_code || '',
              template_name: meta?.template_name || '', pending: meta?.pending || null,
              sku_group_code: meta?.sku_group_code || '',
              sku_group_category_id: meta?.sku_group_category_id || '',
              sku_group_missing: !!meta?.sku_group_missing,
            };
            setForm((p) => ({ ...p, number_value: next, sku, barcode: sku }));
            if (meta?.pending) toast.success(`発番予定: ${sku} — 保存時に正式発番されます`);
            if (saveAfterIssueRef.current) {
              saveAfterIssueRef.current = false;
              persist({}, next);
            }
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
